import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";

import {
  CardContent,
  Grid,
  Card as MuiCard,
  Typography as MuiTypography,
  Alert as MuiAlert,
  Button,
  IconButton,
  CardHeader,
  CardActions,
  Paper,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Skeleton,
  Accordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
} from "@mui/material";
import { spacing, display } from "@mui/system";
import axios from "../../utils/axios";
import { SUPPORT_APP_USER_MODIFY } from "../../constants";
import convertUTC from "../../utils/convertUTC";
import { Edit as EditIcon } from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import { checkPermissions } from "../../utils/checkPermissions";
import { Field, Formik } from "formik";
import {
  FieldName,
  FieldRole,
  FieldEmail,
  FieldStatus,
} from "./components/Fields";
import FieldView from "./components/FieldView";
import * as Yup from "yup";
import { ServerDataGrid } from "../../components/CustomDataGrid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DateRangeFilter } from "../../components/CustomCells";
import useConfirm from "../../components/useConfirm";

const Card = styled(MuiCard)`
  ${spacing};

  box-shadow: none;
`;

const Typography = styled(MuiTypography)(display);

const Alert = styled(MuiAlert)(spacing);

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  padding: theme.spacing(0, 4),
  "&.Mui-expanded": {
    minHeight: "unset",
  },
  "& .Mui-expanded": {
    margin: theme.spacing(3, 0),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(spacing);

const columns = [
  {
    accessorKey: "action",
    filterVariant: "text",
    header: "Action",
    operator: "equals",
  },
  {
    accessorKey: "created_at",
    type: "dateTime",
    header: "Date/Time of Action",
    sortingFn: "datetime",
    Filter: DateRangeFilter,
    operator: "betweenInclusive",
  },
];

const SupportUserActionHistoryTable = ({ userId }) => (
  <ServerDataGrid
    getRowId={(row) => row._id}
    columns={columns}
    apiUrl={`/api/v1/support-users/${userId}/action-history`}
  />
);

const GameItem = ({ game }) => (
  <Grid container spacing={3} alignItems="center">
    <Grid item>
      <Avatar src={game.logo} variant="rounded" />
    </Grid>
    <Grid item>
      <Typography>{game.name}</Typography>
    </Grid>
  </Grid>
);

const SupportUserView = ({ userId }) => {
  const {
    permissions,
    user: { id: profileUserId },
    selectUser,
  } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  const [user, setUser] = useState({});
  const [userOptions, setUserOptions] = useState({});
  const [games, setGames] = useState([]);

  const [edit, setEdit] = useState(false);
  const [editGames, setEditGames] = useState(false);

  const checkCurrentUser = useCallback(
    (user) => {
      if (profileUserId === user.id) {
        selectUser(user);
      }
    },
    [selectUser, profileUserId]
  );

  const getUser = useCallback(async () => {
    try {
      setLoading(true);

      const { data: userOptions } = await axios.options(
        `/api/v1/support-users/${userId}`
      );
      setUserOptions(userOptions);

      const { data: user } = await axios.get(`/api/v1/support-users/${userId}`);
      setUser(user);

      const { data: games } = await axios.get("/api/v1/games/");
      // TODO: Hack for now until games can be acquired only for table view
      setGames(games.entries);

      setLoading(false);
    } catch (error) {
      console.log(error);

      setError(error.messages);

      setLoading(false);
    }
  }, [setLoading, setError, setUser, userId]);

  const [ConfirmUpdateUserDialog, confirmUpdateUserAction] = useConfirm(
    "Are you sure you want to update user?"
  );
  const updateUser = useCallback(
    async (
      { name, role, email, status },
      { setErrors, setStatus, setSubmitting }
    ) => {
      const isConfirmed = await confirmUpdateUserAction();

      if (!isConfirmed) {
        return;
      }

      try {
        const { data } = await axios.post(`/api/v1/support-users/${userId}`, {
          name,
          role,
          email,
          status: Number(status),
        });

        checkCurrentUser(data);

        setUser(data);

        setEdit(false);
      } catch (error) {
        console.log(error);

        const message =
          (error.messages && error.messages.join("\n")) ||
          "Something went wrong";

        setStatus({ success: false });
        setErrors({ submit: message });
        setSubmitting(false);
      }
    },
    [checkCurrentUser, userId]
  );

  const [ConfirmUpdateGamesDialog, confirmUpdateGamesAction] = useConfirm(
    "Are you sure you want to update apps selection?"
  );
  const updateGames = useCallback(
    async ({ games }, { setErrors, setStatus, setSubmitting }) => {
      const isConfirmed = await confirmUpdateGamesAction();

      if (!isConfirmed) {
        return;
      }

      try {
        const { data } = await axios.post(`/api/v1/support-users/${userId}`, {
          games,
        });
        setUser(data);

        setEditGames(false);
      } catch (error) {
        console.log(error);

        const message =
          (error.messages && error.messages.join("\n")) ||
          "Something went wrong";

        setStatus({ success: false });
        setErrors({ submit: message });
        setSubmitting(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    getUser();
  }, [getUser]);

  if (error) {
    return (
      <Alert mb={4} severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={
              loading ? (
                <Skeleton
                  variant="text"
                  width={120}
                  sx={{ fontSize: "1rem" }}
                />
              ) : (
                user.name
              )
            }
            action={
              checkPermissions([SUPPORT_APP_USER_MODIFY], permissions) && (
                <IconButton
                  size="small"
                  onClick={() => setEdit(true)}
                  title="Edit User"
                  disabled={loading || edit || editGames}
                >
                  <EditIcon />
                </IconButton>
              )
            }
          />

          <Formik
            initialValues={{
              name: user.name,
              role: user.role,
              email: user.email,
              status: user.status,
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              name: Yup.string().required("Name is required"),
              role: Yup.string().required("Role is required"),
              email: Yup.string()
                .required("Email is required")
                .email("Must be a valid email"),
              status: Yup.number().required("Status is required"),
            })}
            onSubmit={updateUser}
          >
            {({ errors, resetForm, handleSubmit, isSubmitting, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <CardContent>
                  <Grid container spacing={4}>
                    {errors.submit && (
                      <Grid item xs={12}>
                        <Alert
                          severity="warning"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {errors.submit}
                        </Alert>
                      </Grid>
                    )}
                    <Grid item md={6} xs={12}>
                      <FieldView title="ID" value={user.id} loading={loading} />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldName />
                      ) : (
                        <FieldView
                          title="Name"
                          value={values.name}
                          loading={loading}
                        />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldRole options={userOptions.role} />
                      ) : (
                        <FieldView
                          title="Role"
                          value={
                            userOptions?.role?.find(
                              (role) => role.value === values.role
                            )?.display_name
                          }
                          loading={loading}
                        />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldEmail />
                      ) : (
                        <FieldView
                          title="Email"
                          value={values.email}
                          loading={loading}
                        />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <FieldView
                        title="Last Login Date"
                        value={convertUTC(user.lastLoginAt)}
                        loading={loading}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <FieldView
                        title="Registration Date"
                        value={convertUTC(user.registeredAt)}
                        loading={loading}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldStatus options={userOptions.status} />
                      ) : (
                        <FieldView
                          title="Status"
                          value={
                            userOptions?.status?.find(
                              (status) => status.value === values.status
                            )?.display_name
                          }
                          loading={loading}
                        />
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
                {edit && (
                  <CardActions>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Update User
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => {
                        resetForm();
                        setEdit(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </CardActions>
                )}
              </form>
            )}
          </Formik>
          <ConfirmUpdateUserDialog />
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card>
          <Formik
            initialValues={{
              games: user.games,
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              games: Yup.array(),
            })}
            onSubmit={updateGames}
          >
            {({
              errors,
              resetForm,
              handleSubmit,
              setValues,
              isSubmitting,
              values,
            }) => (
              <form noValidate onSubmit={handleSubmit}>
                <CardHeader
                  title={
                    loading ? (
                      <Skeleton
                        variant="text"
                        width={120}
                        sx={{ fontSize: "1rem" }}
                      />
                    ) : editGames ? (
                      <Grid container spacing={4} alignItems="center">
                        <Grid item>
                          <Typography variant="h6">
                            {`${values?.games?.length} ${
                              values?.games?.length === 1 ? "App" : "Apps"
                            } Selected`}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Button
                            onClick={() =>
                              setValues({
                                games: games.map((game) => game.id),
                              })
                            }
                          >
                            Choose All
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            onClick={() =>
                              setValues({
                                games: [],
                              })
                            }
                          >
                            Remove All
                          </Button>
                        </Grid>
                      </Grid>
                    ) : (
                      `${user?.games?.length} ${
                        user?.games?.length === 1 ? "App" : "Apps"
                      } Selected`
                    )
                  }
                  action={
                    checkPermissions(
                      [SUPPORT_APP_USER_MODIFY],
                      permissions
                    ) && (
                      <IconButton
                        size="small"
                        onClick={() => setEditGames(true)}
                        title="Edit Games"
                        disabled={loading || edit || editGames}
                      >
                        <EditIcon />
                      </IconButton>
                    )
                  }
                />
                <CardContent>
                  <Grid container spacing={8}>
                    {games
                      .filter(
                        (game) => editGames || user.games.includes(game.id)
                      )
                      .map((game) => (
                        <Grid item key={game.id}>
                          {editGames ? (
                            <Field type="checkbox" name="games" value={game.id}>
                              {({ field: { onChange, checked } }) => (
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        name="games"
                                        value={game.id}
                                        checked={checked}
                                        onChange={onChange}
                                      />
                                    }
                                    label={<GameItem game={game} />}
                                  />
                                </FormGroup>
                              )}
                            </Field>
                          ) : (
                            <GameItem game={game} />
                          )}
                        </Grid>
                      ))}
                  </Grid>
                </CardContent>
                {editGames && (
                  <CardActions>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Update Games
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => {
                        resetForm();
                        setEditGames(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </CardActions>
                )}
              </form>
            )}
          </Formik>
          <ConfirmUpdateGamesDialog />
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Paper elevation={0}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} px={4}>
              <Typography variant="h6">Action History</Typography>
            </AccordionSummary>
            <AccordionDetails px={0}>
              <SupportUserActionHistoryTable userId={userId} />
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SupportUserView;
